import { useState } from "react"
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query"
import {
  Search, ChevronLeft, ChevronRight, MoreHorizontal, Shield, Mail, Calendar,
  CreditCard, UserX, UserCheck, Trash2, Edit, Globe, Clock, Languages,
  KeyRound, Building2, Users, FileText, HardDrive, Activity, Loader2,
  Home, Smartphone, MessageSquareHeart, Trophy, Send, MapPin, Phone,
  BedDouble, Bath, Ruler, DollarSign, User, X,
} from "lucide-react"
import { format, formatDistanceToNow } from "date-fns"

import { api } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn, getInitials } from "@/lib/utils"
import { toast } from "sonner"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"

interface AdminUser {
  id: string
  email: string
  full_name: string | null
  avatar_url: string | null
  role: string
  plan_id: string | null
  country_code: string | null
  default_currency: string
  email_verified: boolean
  last_active_at: string | null
  created_at: string
}

interface AdminUserDetail extends AdminUser {
  locale: string
  timezone: string
  date_format: string
  two_fa_enabled: boolean
  stripe_customer_id: string | null
  updated_at: string
  subscription: {
    id: string
    status: string
    billing_cycle: string
    current_period_end: string | null
    cancel_at_period_end: boolean
  } | null
}

interface Plan {
  id: string
  name: string
  display_name: string
}

interface UsageItem {
  used: number
  limit: number | null
  pct: number | null
}

interface UserUsage {
  plan: { name: string; plan_type: string }
  usage: {
    properties: UsageItem
    tenants: UsageItem
    leases: UsageItem
    documents: UsageItem
    storage_gb: UsageItem
  }
  features: Record<string, boolean>
}

interface UserProperty {
  id: string
  name: string
  type: string | null
  address_line1: string | null
  city: string | null
  postcode: string | null
  country_code: string | null
  bedrooms: number | null
  bathrooms: number | null
  size_m2: string | null
  monthly_rent: string | null
  currency: string
  status: string
  furnished: string | null
  created_at: string
}

interface UserTenant {
  id: string
  full_name: string
  email: string | null
  phone: string | null
  nationality: string | null
  employment_status: string | null
  monthly_income: string | null
  income_currency: string | null
  created_at: string
}

interface UserLease {
  id: string
  status: string
  start_date: string
  end_date: string | null
  monthly_rent: string
  currency: string
  lease_type: string | null
  deposit_amount: string | null
  deposit_paid: boolean
  billing_cycle: string
  tenant_id: string
  property_id: string
  property_name: string | null
  tenant_name: string | null
  created_at: string
}

interface UserDocument {
  id: string
  entity_type: string
  entity_id: string
  category: string | null
  file_name: string | null
  file_url: string
  file_size_kb: number | null
  uploaded_at: string
}

interface UserResources {
  properties: UserProperty[]
  tenants: UserTenant[]
  leases: UserLease[]
  documents: UserDocument[]
}

// ─── Usage bar ───────────────────────────────────────────────────────────────

function UsageBar({ item, label, icon: Icon, onClick }: { item: UsageItem; label: string; icon: React.ElementType; onClick?: () => void }) {
  const pct = item.pct ?? 0
  const isOver = pct >= 100
  const isWarning = pct >= 80 && !isOver
  const isClickable = !!onClick && item.used > 0

  return (
    <div
      className={cn("space-y-1.5", isClickable && "cursor-pointer group/bar rounded-lg p-1.5 -m-1.5 hover:bg-muted/60 transition-colors")}
      onClick={isClickable ? onClick : undefined}
    >
      <div className="flex items-center justify-between text-xs">
        <div className={cn("flex items-center gap-1.5 font-medium", isClickable ? "text-primary" : "text-muted-foreground")}>
          <Icon className="h-3.5 w-3.5" />
          {label}
          {isClickable && <span className="text-[10px] opacity-60 group-hover/bar:opacity-100">(view)</span>}
        </div>
        <span className={cn("font-semibold", isOver && "text-red-600", isWarning && "text-amber-600")}>
          {item.used}
          {item.limit !== null ? ` / ${item.limit}` : " / ∞"}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            "h-full rounded-full transition-all",
            isOver ? "bg-red-500" : isWarning ? "bg-amber-500" : "bg-emerald-500"
          )}
          style={{ width: item.limit === null ? "0%" : `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  )
}

// ─── Info row ─────────────────────────────────────────────────────────────────

function InfoRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string | null | undefined }) {
  if (!value) return null
  return (
    <div className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground w-24 shrink-0">{label}</span>
      <span className="text-xs font-medium text-foreground truncate">{value}</span>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AdminUsersPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false)
  const [resourceTab, setResourceTab] = useState<"properties" | "tenants" | "leases" | "documents">("properties")
  const [previewDoc, setPreviewDoc] = useState<{ url: string; name: string } | null>(null)
  const [youtubeUrl, setYoutubeUrl] = useState("")
  const [sendingEmailType, setSendingEmailType] = useState<string | null>(null)

  const qc = useQueryClient()

  const { data: plansData } = useQuery<Plan[]>({
    queryKey: ["plans"],
    queryFn: async () => {
      const { data } = await api.get("/api/plans")
      return data.data ?? []
    },
  })

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "users", page],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/users?page=${page}&limit=15`)
      return data.data
    },
  })

  // Fetch full user detail when sheet is open
  const { data: userDetail, isLoading: detailLoading } = useQuery<AdminUserDetail>({
    queryKey: ["admin", "user-detail", selectedUser?.id],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/users/${selectedUser!.id}`)
      return data.data
    },
    enabled: sheetOpen && !!selectedUser?.id,
  })

  // Fetch user usage stats when sheet is open
  const { data: userUsage, isLoading: usageLoading } = useQuery<UserUsage>({
    queryKey: ["admin", "user-usage", selectedUser?.id],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/users/${selectedUser!.id}/usage`)
      return data.data
    },
    enabled: sheetOpen && !!selectedUser?.id,
  })

  // Fetch user resources when resource dialog is open
  const { data: userResources, isLoading: resourcesLoading } = useQuery<UserResources>({
    queryKey: ["admin", "user-resources", selectedUser?.id],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/users/${selectedUser!.id}/resources`)
      return data.data
    },
    enabled: resourceDialogOpen && !!selectedUser?.id,
  })

  const suspendMutation = useMutation({
    mutationFn: async ({ id, suspended }: { id: string; suspended: boolean }) => {
      const { data } = await api.put(`/api/admin/users/${id}/suspend`, { suspended })
      return data
    },
    onSuccess: (data) => {
      toast.success(data.message)
      qc.invalidateQueries({ queryKey: ["admin", "users"] })
    },
    onError: (err: any) => toast.error(err.response?.data?.error || "Failed action"),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/api/admin/users/${id}`)
      return data
    },
    onSuccess: () => {
      toast.success("User permanently deleted")
      qc.invalidateQueries({ queryKey: ["admin", "users"] })
      setSheetOpen(false)
    },
    onError: (err: any) => toast.error(err.response?.data?.error || "Failed to delete user"),
  })

  const sendEngagementEmail = async (userId: string, type: string) => {
    if (sendingEmailType) return
    setSendingEmailType(type)
    try {
      const body: Record<string, string> = { type }
      if (type === "get_started" && youtubeUrl) body.youtube_playlist_url = youtubeUrl
      await api.post(`/api/admin/users/${userId}/send-email`, body)
      toast.success("Email sent successfully ✓")
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to send email")
    } finally {
      setSendingEmailType(null)
    }
  }

  const users: AdminUser[] = data?.users ?? []
  const pagination = data?.pagination

  const filtered = search
    ? users.filter(
      (u) =>
        u.email.toLowerCase().includes(search.toLowerCase()) ||
        (u.full_name ?? "").toLowerCase().includes(search.toLowerCase())
    )
    : users

  const openUserSheet = (user: AdminUser) => {
    setSelectedUser(user)
    setSheetOpen(true)
  }

  const openResourceDialog = (tab: "properties" | "tenants" | "leases" | "documents") => {
    setResourceTab(tab)
    setResourceDialogOpen(true)
  }

  const planName = (planId: string | null) =>
    plansData?.find((p) => p.id === planId)?.name || "free"

  return (
    <div className="p-8 max-w-[1400px] mx-auto flex flex-col h-[calc(100vh-theme(spacing.16))]">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">User Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {pagination?.total ?? 0} active accounts on the platform
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 rounded-xl bg-card border-border shadow-sm"
            />
          </div>
          <Button variant="outline" className="h-10 rounded-xl shadow-sm">
            Export CSV
          </Button>
        </div>
      </div>

      <div className="flex-1 bg-card border border-border rounded-2xl shadow-sm overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card/95 backdrop-blur-sm z-10 border-b border-border shadow-sm">
              <tr>
                <th className="text-left px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider">User</th>
                <th className="text-left px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider">Role</th>
                <th className="text-left px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider">Access Tier</th>
                <th className="text-left px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider">Last Active</th>
                <th className="text-left px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider">Joined</th>
                <th className="text-right px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider w-20">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4"><Skeleton className="h-10 w-48 rounded-xl" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-5 w-20 rounded-full" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-5 w-20 rounded-full" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-8 w-8 rounded-lg ml-auto" /></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center">
                      <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
                        <Search className="h-6 w-6" />
                      </div>
                      <p className="text-base font-medium text-foreground">No users found</p>
                      <p className="text-sm">We couldn't find anyone matching that search.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((user) => {
                  const tier = planName(user.plan_id)
                  const isSuspended = user.role === "suspended"

                  return (
                    <tr
                      key={user.id}
                      className={cn(
                        "group hover:bg-muted/40 transition-colors cursor-pointer",
                        isSuspended && "opacity-60 bg-red-50/30"
                      )}
                      onClick={() => openUserSheet(user)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 border border-border">
                            <AvatarImage src={user.avatar_url || undefined} />
                            <AvatarFallback className="bg-primary/10 text-primary font-medium">
                              {getInitials(user.full_name || user.email)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                              {user.full_name ?? "—"}
                            </p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <p className="text-xs text-muted-foreground">{user.email}</p>
                              {user.email_verified && (
                                <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" title="Verified email" />
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={user.role === "superadmin" ? "destructive" : "secondary"} className="capitalize">
                          {user.role}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant="outline" className={cn(
                          "capitalize bg-background",
                          tier === "premium" && "border-indigo-500 text-indigo-600 bg-indigo-50",
                          tier === "pro" && "border-blue-500 text-blue-600 bg-blue-50"
                        )}>
                          {tier}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-muted-foreground text-xs">
                        {user.last_active_at
                          ? formatDistanceToNow(new Date(user.last_active_at), { addSuffix: true })
                          : "Never"}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground text-sm font-medium">
                        {format(new Date(user.created_at), "MMM d, yyyy")}
                      </td>
                      <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48 rounded-xl shadow-lg">
                            <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Account Actions</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => openUserSheet(user)} className="cursor-pointer">
                              <Edit className="h-4 w-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => suspendMutation.mutate({ id: user.id, suspended: !isSuspended })}
                              className={cn("cursor-pointer", isSuspended ? "text-emerald-600 focus:text-emerald-600" : "text-amber-600 focus:text-amber-600")}
                            >
                              {isSuspended ? <UserCheck className="h-4 w-4 mr-2" /> : <UserX className="h-4 w-4 mr-2" />}
                              {isSuspended ? "Reactivate User" : "Suspend User"}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                if (confirm("Are you absolutely sure? This cannot be undone.")) {
                                  deleteMutation.mutate(user.id)
                                }
                              }}
                              className="text-red-600 focus:text-red-600 cursor-pointer"
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Delete Permanently
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {pagination && pagination.total_pages > 1 && (
          <div className="bg-muted/30 border-t border-border px-6 py-3 flex items-center justify-between">
            <p className="text-sm font-medium text-muted-foreground">
              Showing page <span className="text-foreground">{pagination.page}</span> of <span className="text-foreground">{pagination.total_pages}</span>
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 rounded-lg shadow-sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 rounded-lg shadow-sm"
                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                disabled={page === pagination.total_pages}
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Slide-out Sheet for Details */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg w-full border-l border-border shadow-2xl p-0 flex flex-col">
          {selectedUser && (
            <>
              <SheetHeader className="p-6 border-b border-border bg-muted/20">
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16 border-2 border-background shadow-sm">
                    <AvatarImage src={selectedUser.avatar_url || undefined} />
                    <AvatarFallback className="bg-primary/10 text-primary font-bold text-xl">
                      {getInitials(selectedUser.full_name || selectedUser.email)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <SheetTitle className="text-xl font-bold truncate">{selectedUser.full_name || "Unknown User"}</SheetTitle>
                    <SheetDescription className="text-xs truncate">{selectedUser.email}</SheetDescription>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Badge variant={selectedUser.role === "superadmin" ? "destructive" : "secondary"} className="capitalize text-[10px] px-1.5 py-0">
                        {selectedUser.role}
                      </Badge>
                      {selectedUser.email_verified && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-emerald-600 border-emerald-300 bg-emerald-50">
                          Verified
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto">
                {detailLoading ? (
                  <div className="p-6 space-y-4">
                    {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8 w-full rounded-xl" />)}
                  </div>
                ) : (
                  <div className="divide-y divide-border">

                    {/* ── Contact & Identity ── */}
                    <div className="p-6 space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Contact & Identity</p>
                      <InfoRow icon={Mail} label="Email" value={userDetail?.email} />
                      <InfoRow icon={Globe} label="Country" value={userDetail?.country_code ?? selectedUser.country_code} />
                      <InfoRow icon={CreditCard} label="Currency" value={userDetail?.default_currency ?? selectedUser.default_currency} />
                      <InfoRow icon={Languages} label="Locale" value={userDetail?.locale} />
                      <InfoRow icon={Clock} label="Timezone" value={userDetail?.timezone} />
                      <InfoRow icon={Calendar} label="Date Format" value={userDetail?.date_format} />
                    </div>

                    {/* ── Security ── */}
                    <div className="p-6 space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Security</p>
                      <InfoRow icon={Shield} label="Role" value={userDetail?.role} />
                      <InfoRow icon={KeyRound} label="2FA" value={userDetail?.two_fa_enabled ? "Enabled" : "Disabled"} />
                      <InfoRow icon={CreditCard} label="Stripe ID" value={userDetail?.stripe_customer_id ?? undefined} />
                    </div>

                    {/* ── Subscription ── */}
                    <div className="p-6 space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Subscription</p>
                      {userDetail?.subscription ? (
                        <>
                          <InfoRow icon={Shield} label="Status" value={userDetail.subscription.status} />
                          <InfoRow icon={CreditCard} label="Cycle" value={userDetail.subscription.billing_cycle} />
                          {userDetail.subscription.current_period_end && (
                            <InfoRow
                              icon={Calendar}
                              label="Period ends"
                              value={format(new Date(userDetail.subscription.current_period_end), "MMM d, yyyy")}
                            />
                          )}
                          {userDetail.subscription.cancel_at_period_end && (
                            <p className="text-xs text-amber-600 font-medium mt-1">Cancels at period end</p>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-muted-foreground">No active subscription — on free plan</p>
                      )}
                    </div>

                    {/* ── Activity Timestamps ── */}
                    <div className="p-6 space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Activity</p>
                      <div className="flex items-center gap-3 py-2 border-b border-border/50">
                        <Activity className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-xs text-muted-foreground w-24 shrink-0">Last active</span>
                        <div className="text-xs font-medium">
                          {selectedUser.last_active_at ? (
                            <>
                              <span>{format(new Date(selectedUser.last_active_at), "MMM d, yyyy HH:mm")}</span>
                              <span className="text-muted-foreground ml-1.5">({formatDistanceToNow(new Date(selectedUser.last_active_at), { addSuffix: true })})</span>
                            </>
                          ) : (
                            <span className="text-muted-foreground">Never logged in</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 py-2 border-b border-border/50">
                        <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-xs text-muted-foreground w-24 shrink-0">Joined</span>
                        <span className="text-xs font-medium">{format(new Date(selectedUser.created_at), "MMM d, yyyy HH:mm")}</span>
                      </div>
                      {userDetail?.updated_at && (
                        <div className="flex items-center gap-3 py-2">
                          <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-xs text-muted-foreground w-24 shrink-0">Profile updated</span>
                          <span className="text-xs font-medium">{format(new Date(userDetail.updated_at), "MMM d, yyyy HH:mm")}</span>
                        </div>
                      )}
                      <div className="mt-2 pt-2">
                        <p className="text-[10px] font-mono text-muted-foreground/60">ID: {selectedUser.id}</p>
                      </div>
                    </div>

                    {/* ── Resource Usage ── */}
                    <div className="p-6 space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Plan Usage</p>
                        {usageLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                      </div>

                      {usageLoading ? (
                        <div className="space-y-3">
                          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full rounded" />)}
                        </div>
                      ) : userUsage ? (
                        <>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">
                              {userUsage.plan.name} plan
                            </Badge>
                            <span className="text-[10px] text-muted-foreground">({userUsage.plan.plan_type})</span>
                          </div>
                          <div className="space-y-3">
                            <UsageBar item={userUsage.usage.properties} label="Properties" icon={Building2} onClick={() => openResourceDialog("properties")} />
                            <UsageBar item={userUsage.usage.tenants} label="Tenants" icon={Users} onClick={() => openResourceDialog("tenants")} />
                            <UsageBar item={userUsage.usage.leases} label="Leases" icon={FileText} onClick={() => openResourceDialog("leases")} />
                            <UsageBar item={userUsage.usage.documents} label="Documents" icon={FileText} onClick={() => openResourceDialog("documents")} />
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-1.5 text-muted-foreground font-medium">
                                  <HardDrive className="h-3.5 w-3.5" />
                                  Storage
                                </div>
                                <span className={cn(
                                  "font-semibold",
                                  (userUsage.usage.storage_gb.pct ?? 0) >= 100 && "text-red-600",
                                  (userUsage.usage.storage_gb.pct ?? 0) >= 80 && (userUsage.usage.storage_gb.pct ?? 0) < 100 && "text-amber-600"
                                )}>
                                  {userUsage.usage.storage_gb.used.toFixed(3)} GB
                                  {userUsage.usage.storage_gb.limit !== null
                                    ? ` / ${userUsage.usage.storage_gb.limit} GB`
                                    : " / ∞"}
                                </span>
                              </div>
                              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                                <div
                                  className={cn(
                                    "h-full rounded-full transition-all",
                                    (userUsage.usage.storage_gb.pct ?? 0) >= 100 ? "bg-red-500"
                                      : (userUsage.usage.storage_gb.pct ?? 0) >= 80 ? "bg-amber-500"
                                        : "bg-emerald-500"
                                  )}
                                  style={{
                                    width: userUsage.usage.storage_gb.limit === null
                                      ? "0%"
                                      : `${Math.min(100, userUsage.usage.storage_gb.pct ?? 0)}%`
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <p className="text-xs text-muted-foreground">Usage data unavailable</p>
                      )}
                    </div>

                    {/* ── Engagement Emails ── */}
                    <div className="p-6 space-y-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Engagement Emails</p>
                      </div>
                      <p className="text-[11px] text-muted-foreground leading-relaxed">
                        Send a targeted email directly to this user.
                      </p>

                      {/* Get Started */}
                      <div className="rounded-xl border border-teal-200 bg-teal-50/50 p-3 space-y-2">
                        <div className="flex items-start gap-2.5">
                          <div className="h-8 w-8 rounded-lg bg-teal-100 flex items-center justify-center shrink-0">
                            <Home className="h-4 w-4 text-teal-700" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-teal-900">Get Started Nudge</p>
                            <p className="text-[11px] text-teal-700 leading-relaxed">For users who haven't added properties yet. Includes a YouTube tutorial link.</p>
                          </div>
                        </div>
                        <input
                          type="url"
                          placeholder="YouTube playlist URL (optional)"
                          value={youtubeUrl}
                          onChange={(e) => setYoutubeUrl(e.target.value)}
                          className="w-full text-xs rounded-lg border border-teal-200 bg-white px-3 py-1.5 outline-none focus:ring-1 focus:ring-teal-400 text-foreground placeholder:text-muted-foreground"
                        />
                        <Button
                          size="sm"
                          className="w-full h-8 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold"
                          onClick={() => sendEngagementEmail(selectedUser!.id, "get_started")}
                          disabled={sendingEmailType !== null}
                        >
                          {sendingEmailType === "get_started" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                          Send Get Started Email
                        </Button>
                      </div>

                      {/* Mobile App */}
                      <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-3 space-y-2">
                        <div className="flex items-start gap-2.5">
                          <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                            <Smartphone className="h-4 w-4 text-blue-700" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-blue-900">Download Mobile App</p>
                            <p className="text-[11px] text-blue-700 leading-relaxed">Invite them to download the iOS or Android app with feature highlights.</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="w-full h-8 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold"
                          onClick={() => sendEngagementEmail(selectedUser!.id, "mobile_app")}
                          disabled={sendingEmailType !== null}
                        >
                          {sendingEmailType === "mobile_app" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                          Send Mobile App Email
                        </Button>
                      </div>

                      {/* Feedback */}
                      <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-3 space-y-2">
                        <div className="flex items-start gap-2.5">
                          <div className="h-8 w-8 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                            <MessageSquareHeart className="h-4 w-4 text-violet-700" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-violet-900">Request Feedback</p>
                            <p className="text-[11px] text-violet-700 leading-relaxed">A personal, founder-style email. User replies go to <span className="font-mono">policy-admin@rent-keep.com</span>.</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="w-full h-8 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold"
                          onClick={() => sendEngagementEmail(selectedUser!.id, "feedback")}
                          disabled={sendingEmailType !== null}
                        >
                          {sendingEmailType === "feedback" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                          Send Feedback Request
                        </Button>
                      </div>

                      {/* Milestone */}
                      <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 space-y-2">
                        <div className="flex items-start gap-2.5">
                          <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                            <Trophy className="h-4 w-4 text-amber-700" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-amber-900">Milestone Praise 🎉</p>
                            <p className="text-[11px] text-amber-700 leading-relaxed">Celebrates their progress with live stats — properties, tenants, days active. Feel-good boost.</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="w-full h-8 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold"
                          onClick={() => sendEngagementEmail(selectedUser!.id, "milestone")}
                          disabled={sendingEmailType !== null}
                        >
                          {sendingEmailType === "milestone" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                          Send Milestone Email
                        </Button>
                      </div>
                    </div>

                    {/* ── Account Actions ── */}
                    <div className="p-6 space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Account Actions</p>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start h-10 rounded-xl",
                          selectedUser.role === "suspended"
                            ? "text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                            : "text-amber-600 border-amber-200 hover:bg-amber-50"
                        )}
                        onClick={() => suspendMutation.mutate({ id: selectedUser.id, suspended: selectedUser.role !== "suspended" })}
                        disabled={suspendMutation.isPending}
                      >
                        {selectedUser.role === "suspended"
                          ? <><UserCheck className="h-4 w-4 mr-2" /> Reactivate Account</>
                          : <><UserX className="h-4 w-4 mr-2" /> Suspend Account</>
                        }
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full justify-start h-10 rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => {
                          if (confirm("Are you absolutely sure? This cannot be undone.")) {
                            deleteMutation.mutate(selectedUser.id)
                          }
                        }}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete Permanently
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* ── Resource Detail Dialog ── */}
      <Dialog open={resourceDialogOpen} onOpenChange={setResourceDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
            <DialogTitle className="text-lg font-bold">
              {selectedUser?.full_name || selectedUser?.email} — Resources
            </DialogTitle>
          </DialogHeader>

          {resourcesLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
            </div>
          ) : (
            <Tabs value={resourceTab} onValueChange={(v) => setResourceTab(v as typeof resourceTab)} className="flex flex-col flex-1 min-h-0">
              <TabsList className="mx-6 mt-4 shrink-0 flex">
                <TabsTrigger value="properties" className="flex-1">
                  Properties ({userResources?.properties?.length ?? 0})
                </TabsTrigger>
                <TabsTrigger value="tenants" className="flex-1">
                  Tenants ({userResources?.tenants?.length ?? 0})
                </TabsTrigger>
                <TabsTrigger value="leases" className="flex-1">
                  Leases ({userResources?.leases?.length ?? 0})
                </TabsTrigger>
                <TabsTrigger value="documents" className="flex-1">
                  Documents ({userResources?.documents?.length ?? 0})
                </TabsTrigger>
              </TabsList>

              {/* Properties */}
              <TabsContent value="properties" className="flex-1 overflow-y-auto px-6 pb-6 mt-4 space-y-3">
                {!userResources?.properties?.length ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No properties found</p>
                ) : userResources.properties.map((p) => (
                  <div key={p.id} className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-primary shrink-0" />
                        <span className="font-semibold text-sm">{p.name}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {p.type && <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">{p.type}</Badge>}
                        <Badge variant={p.status === "occupied" ? "default" : "secondary"} className="text-[10px] px-1.5 py-0 capitalize">{p.status}</Badge>
                      </div>
                    </div>
                    {(p.address_line1 || p.city) && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {[p.address_line1, p.city, p.postcode, p.country_code].filter(Boolean).join(", ")}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground pt-1">
                      {p.bedrooms != null && <span className="flex items-center gap-1"><BedDouble className="h-3 w-3" />{p.bedrooms} bed</span>}
                      {p.bathrooms != null && <span className="flex items-center gap-1"><Bath className="h-3 w-3" />{p.bathrooms} bath</span>}
                      {p.size_m2 && <span className="flex items-center gap-1"><Ruler className="h-3 w-3" />{p.size_m2} m²</span>}
                      {p.monthly_rent && <span className="flex items-center gap-1 font-semibold text-foreground"><DollarSign className="h-3 w-3" />{p.currency} {p.monthly_rent}/mo</span>}
                      {p.furnished && <span className="capitalize">{p.furnished}</span>}
                    </div>
                    <p className="text-[10px] text-muted-foreground/60 font-mono">ID: {p.id}</p>
                  </div>
                ))}
              </TabsContent>

              {/* Tenants */}
              <TabsContent value="tenants" className="flex-1 overflow-y-auto px-6 pb-6 mt-4 space-y-3">
                {!userResources?.tenants?.length ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No tenants found</p>
                ) : userResources.tenants.map((t) => (
                  <div key={t.id} className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-primary shrink-0" />
                      <span className="font-semibold text-sm">{t.full_name}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {t.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{t.email}</span>}
                      {t.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{t.phone}</span>}
                      {t.nationality && <span>Nationality: <span className="text-foreground">{t.nationality}</span></span>}
                      {t.employment_status && <span>Employment: <span className="text-foreground capitalize">{t.employment_status}</span></span>}
                      {t.monthly_income && <span>Income: <span className="text-foreground font-semibold">{t.income_currency} {t.monthly_income}/mo</span></span>}
                    </div>
                    <p className="text-[10px] text-muted-foreground/60 font-mono">ID: {t.id}</p>
                  </div>
                ))}
              </TabsContent>

              {/* Leases */}
              <TabsContent value="leases" className="flex-1 overflow-y-auto px-6 pb-6 mt-4 space-y-3">
                {!userResources?.leases?.length ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No leases found</p>
                ) : userResources.leases.map((l) => (
                  <div key={l.id} className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-0.5">
                        <p className="font-semibold text-sm">{l.property_name ?? "Unknown Property"}</p>
                        <p className="text-xs text-muted-foreground">{l.tenant_name ?? "Unknown Tenant"}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {l.lease_type && <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">{l.lease_type}</Badge>}
                        <Badge variant={l.status === "active" ? "default" : "secondary"} className="text-[10px] px-1.5 py-0 capitalize">{l.status}</Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>Rent: <span className="text-foreground font-semibold">{l.currency} {l.monthly_rent}/mo</span></span>
                      <span>Billing: <span className="text-foreground capitalize">{l.billing_cycle}</span></span>
                      <span>Start: <span className="text-foreground">{format(new Date(l.start_date), "MMM d, yyyy")}</span></span>
                      {l.end_date && <span>End: <span className="text-foreground">{format(new Date(l.end_date), "MMM d, yyyy")}</span></span>}
                      {l.deposit_amount && (
                        <span>
                          Deposit: <span className="text-foreground">{l.currency} {l.deposit_amount}</span>{" "}
                          {l.deposit_paid
                            ? <span className="text-emerald-600">(paid)</span>
                            : <span className="text-amber-600">(unpaid)</span>}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground/60 font-mono">ID: {l.id}</p>
                  </div>
                ))}
              </TabsContent>

              {/* Documents */}
              <TabsContent value="documents" className="flex-1 overflow-y-auto px-6 pb-6 mt-4 space-y-3">
                {!userResources?.documents?.length ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No documents found</p>
                ) : userResources.documents.map((d) => (
                  <div key={d.id} className="rounded-xl border border-border bg-card p-4 flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary shrink-0" />
                        <span className="font-semibold text-sm truncate max-w-xs" title={d.file_name ?? "Attachment"}>
                          {d.file_name ?? "Attachment"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">
                          {d.entity_type}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {d.category && <span className="capitalize text-foreground font-medium">{d.category.replace(/_/g, " ")}</span>}
                      {d.file_size_kb != null && <span>{(d.file_size_kb / 1024).toFixed(2)} MB</span>}
                      <span>Uploaded on {format(new Date(d.uploaded_at), "MMM d, yyyy")}</span>
                      <button 
                        onClick={() => setPreviewDoc({ url: d.file_url, name: d.file_name ?? "Document" })}
                        className="text-primary hover:underline ml-auto flex items-center gap-1 font-medium"
                      >
                        View locally
                      </button>
                    </div>
                    <p className="text-[10px] text-muted-foreground/60 font-mono pt-1">ID: {d.id}</p>
                  </div>
                ))}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Document Preview Dialog ── */}
      <Dialog open={!!previewDoc} onOpenChange={(open) => !open && setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl w-full h-[85vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b border-border shrink-0">
            <DialogTitle className="text-lg font-bold truncate">
              Preview: {previewDoc?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 bg-muted/30 relative">
            {previewDoc && (
              <iframe
                src={previewDoc.url}
                className="w-full h-full border-0 absolute inset-0"
                title={previewDoc.name}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
